import { useState } from "react";
import axios from "axios";
import { validateDatasetMetadata } from "./validateMetadata";

/**
 * Hook for handling the submit review workflow
 *
 * Workflow:
 * 1. Validate metadata against metadata.yaml requirements
 * 2. If communityId is provided (status=draft): Create new review request with community
 * 3. If no communityId (status=draft_with_review): Find existing review and submit it
 * 4. Submit the request
 *
 * @returns {Object} - { loading, error, submitForReview, validationErrors }
 */
export const useSubmitReview = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [validationErrors, setValidationErrors] = useState([]);

  const submitForReview = async (draft, recordId, communityId) => {
    setLoading(true);
    setError(null);
    setValidationErrors([]);

    try {
      // Step 1: Validate metadata
      const metadata = draft?.metadata;
      const validation = validateDatasetMetadata(metadata);

      if (!validation.isValid) {
        setValidationErrors(validation.errors);
        setError({ type: "validation", messages: validation.errors });
        setLoading(false);
        return { success: false, validationErrors: validation.errors };
      }

      let submitActionUrl;

      if (communityId) {
        // Step 2a: Create a new review request with community (for status="draft")
        const createReviewResponse = await axios.put(
          `/api/records/${recordId}/draft/review`,
          {
            type: "community-submission",
            receiver: {
              community: communityId
            }
          }
        );
        submitActionUrl = createReviewResponse.data.links.actions.submit;
      } else {
        // Step 2b: Find existing review request to submit (for status="draft_with_review")
        // First check for submitted requests
        const requestsResponse = await axios.get(
          `/api/requests/?topic=record:${recordId}`
        );
        const requests = requestsResponse.data.hits.hits;

        // Look for community-submission request with status=submitted
        const submittedRequest = requests.find(
          (r) => r.type === "community-submission" && r.status === "submitted"
        );

        if (submittedRequest) {
          submitActionUrl = submittedRequest.links.actions.submit;
        } else {
          // Try to find a "created" request that can be submitted
          const createdRequest = requests.find(
            (r) => r.type === "community-submission" && r.status === "created"
          );
          console.log("CREATED REQUEST:")
          console.log(createdRequest);
          if (createdRequest) {
            submitActionUrl = createdRequest.links.actions.submit;
          } else {
            // Fallback: try to create review using the parent.review from draft
            // This handles cases where the review already exists but wasn't captured in requests
            const parentReview = draft?.parent?.review;
            if (parentReview && parentReview.id) {
              submitActionUrl = `/api/requests/${parentReview.id}/actions/submit`;
            } else {
              throw new Error("No review request found to submit");
            }
          }
        }
      }

      // Step 3: Submit the request
      await axios.post(submitActionUrl);

      setLoading(false);
      return { success: true };
    } catch (err) {
      console.error("Error submitting for review:", err);
      const errorMessage = err.response?.data?.message || err.message || "Unknown error occurred";
      setError({ type: "api", message: errorMessage });
      setLoading(false);
      return { success: false, error: errorMessage };
    }
  };

  return {
    loading,
    error,
    validationErrors,
    submitForReview,
  };
};
